/**
 * ConversionCTA — the single, consistent CTA control for SEO pages.
 *
 * Wraps <TrackedLink> so every CTA fires a typed funnel event with non-sensitive
 * props (source_page / page_type / competitor / cta_text / destination / plan).
 * `cta.external` opens booking links in a new tab. A page composes one above the
 * fold, one mid-page, and one near the bottom (see ConversionCtaBlock).
 */
import type { ReactNode } from "react";
import { cn } from "@/lib/utils";
import { TrackedLink } from "@/components/analytics/tracked-link";
import type { SeoCta } from "@/lib/seo/content/types";
import type { SeoPageType } from "@/lib/analytics/events";

export interface CtaContext {
  source_page: string;
  page_type: SeoPageType;
  competitor?: string;
}

const PRIMARY =
  "inline-flex items-center justify-center px-5 py-3 bg-[color:var(--ink)] text-white text-[14px] font-medium hover:bg-black transition";
const SECONDARY =
  "inline-flex items-center justify-center px-5 py-3 border rule text-[14px] font-medium ink-2 hover:border-[color:var(--ink)] hover:text-[color:var(--ink)] transition";

export function ConversionCta({
  cta,
  context,
  variant = "primary",
  className,
}: {
  cta: SeoCta;
  context: CtaContext;
  variant?: "primary" | "secondary";
  className?: string;
}) {
  return (
    <TrackedLink
      href={cta.href}
      event={cta.event}
      eventProps={{
        source_page: context.source_page,
        page_type: context.page_type,
        competitor: context.competitor,
        destination: cta.destination,
        cta_text: cta.label,
        plan: cta.plan,
        cta_location: variant === "primary" ? "primary" : "secondary",
      }}
      {...(cta.external
        ? { target: "_blank", rel: "noopener noreferrer" }
        : {})}
      className={cn(variant === "primary" ? PRIMARY : SECONDARY, className)}
    >
      {cta.label}
    </TrackedLink>
  );
}

/**
 * The prominent closing CTA section: heading, one line of body, a primary CTA
 * and an optional secondary. Used as the final conversion block on every page.
 */
export function ConversionCtaBlock({
  heading,
  body,
  primary,
  secondary,
  context,
  footnote,
}: {
  heading: string;
  body: string;
  primary: SeoCta;
  secondary?: SeoCta;
  context: CtaContext;
  /** Small print under the buttons — e.g. the <TrialNote /> disclaimer. */
  footnote?: ReactNode;
}) {
  return (
    <section className="border-b rule bg-paper-2">
      <div className="max-w-[1240px] mx-auto px-6 lg:px-8 py-20 text-center">
        <h2 className="font-display font-semibold text-[28px] lg:text-[36px] tracking-[-0.025em] leading-[1.08] max-w-[24ch] mx-auto">
          {heading}
        </h2>
        <p className="mt-4 text-[15px] ink-2 leading-relaxed max-w-[60ch] mx-auto">
          {body}
        </p>
        <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
          <ConversionCta cta={primary} context={context} variant="primary" />
          {secondary && (
            <ConversionCta cta={secondary} context={context} variant="secondary" />
          )}
        </div>
        {footnote && (
          <div className="mt-5 flex justify-center [&_p]:mx-auto [&_p]:text-center">
            {footnote}
          </div>
        )}
      </div>
    </section>
  );
}
